/**
 * @file Glisser-deposer des armes (doigt ET souris) partage par le coffre et les emplacements : equiper, retirer, echanger, fusionner.
 * Doigt : maintien court (la vignette se « soulève » + vibration) puis glisser ; avant le maintien, un geste est un defilement. Le depot est
 * decide d'apres la VIGNETTE FANTOME (la cible est celle qu'elle recouvre le plus, pas celle sous le doigt qui est masquee par la main),
 * la liste defile toute seule quand le fantome approche du bord haut / bas.
 */
import type { Uid } from '@shared/types';

/** Ce que l'on tient : une arme du coffre (`slot` = null) ou d'un emplacement. */
export interface DragSource {
  uid: Uid;
  slot: number | null;
  el: HTMLElement;
  /** Faux : seul le tap compte (emplacement vide ou verrouille). */
  draggable: boolean;
}

export type DropTarget = { kind: 'tile'; uid: Uid; el: HTMLElement } | { kind: 'slot'; slot: number; locked: boolean; el: HTMLElement } | { kind: 'chest'; el: HTMLElement };

export interface DragHooks {
  /** Appui qui commence (ferme les menus ouverts). */
  start(): void;
  /** Surbrillance verte (depot possible) ou rouge. */
  canDrop(src: DragSource, target: DropTarget): boolean;
  /** Appele pour toute cible survolee au relachement (meme refusee : permet d'expliquer le refus). */
  drop(src: DragSource, target: DropTarget): void;
  tap(src: DragSource): void;
  longPress(src: DragSource): void;
  /** Petite vibration quand la vignette se souleve (respecte le reglage « Vibrations »). */
  lift?(): void;
}

/** Deplacement minimal (px) avant qu'un geste devienne un glisser. */
const DRAG_START_PX = 6;
/** Doigt : maintien avant que le geste devienne un glisser (plus court = plus reactif, assez long pour ne pas voler le defilement). */
const TOUCH_ARM_MS = 150;
const LONG_PRESS_MS = 600;
/** Le fantome est affiche au-dessus du doigt (la main ne le cache pas) : fraction de sa hauteur. */
const GHOST_LIFT = 0.85;
/** Recouvrement minimal (part du fantome) pour retenir une cible hors de son centre. */
const MIN_OVERLAP = 0.28;
/** Defilement automatique : zone (px) au bord du conteneur et vitesse maximale (px / frame). */
const EDGE_ZONE = 56;
const EDGE_SPEED = 14;

interface State {
  src: DragSource;
  pointerId: number;
  sx: number;
  sy: number;
  x: number;
  y: number;
  touch: boolean;
  armed: boolean;
  dragging: boolean;
  long: boolean;
  ghost: HTMLElement | null;
  hover: HTMLElement | null;
  armTimer: number | null;
  longTimer: number | null;
  raf: number | null;
  scroller: HTMLElement | null;
}

const area = (r: DOMRect): number => Math.max(0, r.width) * Math.max(0, r.height);
const overlap = (a: DOMRect, b: DOMRect): number => {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
};

export class WeaponDrag {
  private st: State | null = null;
  private readonly cleanups: Array<() => void> = [];
  /** Instant du dernier glisser termine : le « clic » qui suit un relachement ne doit pas compter comme un tap. */
  lastDragEnd = -Infinity;

  constructor(
    /** Racine dans laquelle chercher les cibles (l'ecran Armes). */
    private readonly root: HTMLElement,
    private readonly hooks: DragHooks,
  ) {}

  /** Rend `el` glissable / touchable. `source()` retourne l'arme qu'il porte (`draggable` faux : seul le tap compte). */
  attach(el: HTMLElement, source: () => DragSource | null): void {
    const down = (e: PointerEvent): void => this.onDown(e, el, source);
    const move = (e: PointerEvent): void => this.onMove(e);
    const up = (e: PointerEvent): void => this.onUp(e);
    const cancel = (): void => this.end();
    const touchMove = (e: TouchEvent): void => {
      // geste arme : plus aucun defilement natif (sinon le navigateur annule le glisser)
      if (this.st?.armed && e.cancelable) e.preventDefault();
    };
    const noMenu = (e: Event): void => {
      if (this.st?.touch) e.preventDefault();
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', cancel);
    el.addEventListener('touchmove', touchMove, { passive: false });
    el.addEventListener('contextmenu', noMenu);
    this.cleanups.push(() => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', cancel);
      el.removeEventListener('touchmove', touchMove);
      el.removeEventListener('contextmenu', noMenu);
    });
  }

  destroy(): void {
    this.end();
    for (const c of this.cleanups) c();
    this.cleanups.length = 0;
  }

  get active(): boolean {
    return this.st?.dragging === true;
  }

  // ------------------------------------------------------------------------------------------------ gestes

  private onDown(e: PointerEvent, el: HTMLElement, source: () => DragSource | null): void {
    if (this.st || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const src = source();
    if (!src) return;
    this.hooks.start();
    const touch = e.pointerType !== 'mouse';
    const st: State = { src, pointerId: e.pointerId, sx: e.clientX, sy: e.clientY, x: e.clientX, y: e.clientY, touch, armed: !touch && src.draggable, dragging: false, long: false, ghost: null, hover: null, armTimer: null, longTimer: null, raf: null, scroller: el.closest<HTMLElement>('.sheet-body') };
    if (touch && src.draggable) {
      st.armTimer = window.setTimeout(() => {
        st.armed = true;
        el.classList.add('lifted');
        this.hooks.lift?.();
      }, TOUCH_ARM_MS);
    }
    if (src.draggable) st.longTimer = window.setTimeout(() => {
      if (st.dragging) return;
      st.long = true;
      el.classList.remove('lifted');
      this.hooks.longPress(src);
    }, LONG_PRESS_MS);
    this.st = st;
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* pointeur deja libere */
    }
  }

  private onMove(e: PointerEvent): void {
    const st = this.st;
    if (!st || e.pointerId !== st.pointerId) return;
    st.x = e.clientX;
    st.y = e.clientY;
    if (!st.dragging) {
      if (Math.hypot(st.x - st.sx, st.y - st.sy) < DRAG_START_PX) return;
      if (!st.armed || st.long) {
        // doigt qui defile avant le maintien (ou menu deja ouvert) : pas un glisser
        this.end();
        return;
      }
      this.startDrag(st);
    }
    this.updateGhost(st);
  }

  private onUp(e: PointerEvent): void {
    const st = this.st;
    if (!st || e.pointerId !== st.pointerId) return;
    st.x = e.clientX;
    st.y = e.clientY;
    if (st.dragging) {
      this.updateGhost(st);
      const target = this.targetFor(st);
      const src = st.src;
      this.end();
      this.lastDragEnd = performance.now();
      if (target) this.hooks.drop(src, target);
      return;
    }
    const src = st.src;
    const wasTap = !st.long;
    this.end();
    if (wasTap) this.hooks.tap(src);
  }

  private startDrag(st: State): void {
    st.dragging = true;
    if (st.longTimer !== null) window.clearTimeout(st.longTimer);
    st.longTimer = null;
    const el = st.src.el;
    el.classList.remove('lifted');
    el.classList.add('dragging');
    // on fantomise la vignette de l'arme (celle d'un emplacement est dans une carte plus grande avec ses statistiques)
    const visual = el.matches('.tile') ? el : (el.querySelector<HTMLElement>('.tile') ?? el);
    const ghost = visual.cloneNode(true) as HTMLElement;
    ghost.classList.add('tile-ghost');
    ghost.classList.remove('dragging', 'selected', 'lifted', 'mini');
    ghost.removeAttribute('data-uid');
    ghost.removeAttribute('data-slot');
    ghost.removeAttribute('data-tutorial');
    ghost.style.pointerEvents = 'none';
    ghost.style.width = `${Math.min(88, Math.max(64, visual.getBoundingClientRect().width)).toFixed(0)}px`;
    document.body.append(ghost);
    st.ghost = ghost;
    const loop = (): void => {
      const cur = this.st;
      if (!cur || cur !== st || !st.dragging) return;
      this.autoScroll(st);
      st.raf = window.requestAnimationFrame(loop);
    };
    st.raf = window.requestAnimationFrame(loop);
  }

  private ghostRect(st: State): DOMRect | null {
    return st.ghost ? st.ghost.getBoundingClientRect() : null;
  }

  private updateGhost(st: State): void {
    const g = st.ghost;
    if (!g) return;
    g.style.transform = `translate3d(${(st.x - g.offsetWidth / 2).toFixed(1)}px, ${(st.y - g.offsetHeight * GHOST_LIFT).toFixed(1)}px, 0)`;
    const target = this.targetFor(st);
    const el = target?.el ?? null;
    if (el !== st.hover) {
      st.hover?.classList.remove('drop-ok', 'drop-bad');
      st.hover = el;
    }
    if (target && el) {
      const ok = this.hooks.canDrop(st.src, target);
      el.classList.toggle('drop-ok', ok);
      el.classList.toggle('drop-bad', !ok && target.kind !== 'chest');
    }
  }

  /** Cible = celle que le fantome recouvre le plus (centre compris), a defaut la zone du coffre si le centre y est. */
  private targetFor(st: State): DropTarget | null {
    const g = this.ghostRect(st);
    if (!g) return null;
    const cx = g.left + g.width / 2;
    const cy = g.top + g.height / 2;
    let best: DropTarget | null = null;
    let bestScore = 0;
    let chest: DropTarget | null = null;
    for (const el of this.root.querySelectorAll<HTMLElement>('[data-uid]:not([data-slot]), [data-slot], [data-chest]')) {
      if (el === st.src.el || el.classList.contains('tile-ghost')) continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      if (el.dataset.chest !== undefined) {
        if (cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom) chest = { kind: 'chest', el };
        continue;
      }
      const o = overlap(g, r);
      const inside = cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom;
      if (!inside && o < MIN_OVERLAP * Math.min(area(g), area(r))) continue;
      // le centre dans la cible compte double : evite de « sauter » vers une voisine plus recouverte
      const score = o + (inside ? area(g) : 0);
      if (score <= bestScore) continue;
      bestScore = score;
      if (el.dataset.slot !== undefined) best = { kind: 'slot', slot: Number(el.dataset.slot), locked: el.dataset.locked === 'true', el };
      else if (el.dataset.uid !== undefined) best = { kind: 'tile', uid: el.dataset.uid, el };
    }
    return best ?? chest;
  }

  private autoScroll(st: State): void {
    const sc = st.scroller;
    const g = this.ghostRect(st);
    if (!sc || !g) return;
    const r = sc.getBoundingClientRect();
    if (st.y > r.bottom - EDGE_ZONE && st.y < r.bottom + 40) sc.scrollTop += EDGE_SPEED * Math.min(1, (st.y - (r.bottom - EDGE_ZONE)) / EDGE_ZONE);
    else if (st.y < r.top + EDGE_ZONE && st.y > r.top - 40) sc.scrollTop -= EDGE_SPEED * Math.min(1, (r.top + EDGE_ZONE - st.y) / EDGE_ZONE);
    else return;
    this.updateGhost(st);
  }

  private end(): void {
    const st = this.st;
    if (!st) return;
    this.st = null;
    if (st.armTimer !== null) window.clearTimeout(st.armTimer);
    if (st.longTimer !== null) window.clearTimeout(st.longTimer);
    if (st.raf !== null) window.cancelAnimationFrame(st.raf);
    st.hover?.classList.remove('drop-ok', 'drop-bad');
    st.ghost?.remove();
    st.src.el.classList.remove('dragging', 'lifted');
    try {
      st.src.el.releasePointerCapture(st.pointerId);
    } catch {
      /* deja relache */
    }
  }
}
